import { Dialog, Transition } from '@headlessui/react';
import { LEAD_LOST_REASONS, type LeadLostReason } from 'lib/lead-lost-reasons';
import { Fragment, useEffect, useState } from 'react';

import { OVERLAY_Z_INDEX } from 'utils/overlay-layers';

type CloseLeadModalProps = {
  open: boolean;
  leadName?: string | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    lostReason: LeadLostReason;
    lostReasonNotes: string;
  }) => void | Promise<void>;
};

export default function CloseLeadModal({
  open,
  leadName,
  loading = false,
  onClose,
  onConfirm,
}: CloseLeadModalProps) {
  const [lostReason, setLostReason] = useState<LeadLostReason | ''>('');
  const [lostReasonNotes, setLostReasonNotes] = useState('');

  useEffect(() => {
    if (!open) {
      setLostReason('');
      setLostReasonNotes('');
    }
  }, [open]);

  const selected = LEAD_LOST_REASONS.find(
    (reason) => reason.value === lostReason,
  );
  const notesRequired = lostReason === 'other';
  const notesValid = !notesRequired || lostReasonNotes.trim().length > 0;
  const canSubmit = Boolean(lostReason) && notesValid && !loading;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        open={open}
        className="relative z-[2147483647]"
        style={{ zIndex: OVERLAY_Z_INDEX }}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-xl bg-white p-6 shadow-xl transition-all">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Why was this lead lost?
                </Dialog.Title>
                <p className="mt-2 text-sm text-gray-600">
                  {leadName
                    ? `Record why ${leadName} did not convert. This helps the team spot patterns in lost business.`
                    : 'Record why this lead did not convert. This helps the team spot patterns in lost business.'}
                </p>

                <div className="mt-4">
                  <label
                    htmlFor="lost-reason"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Lost reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="lost-reason"
                    value={lostReason}
                    onChange={(event) =>
                      setLostReason(event.target.value as LeadLostReason)
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
                  >
                    <option value="">Select a reason…</option>
                    {LEAD_LOST_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                  {selected?.hint ? (
                    <p className="mt-1 text-xs text-gray-500">
                      {selected.hint}
                    </p>
                  ) : null}
                </div>

                <div className="mt-4">
                  <label
                    htmlFor="lost-reason-notes"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {notesRequired ? (
                      <>
                        Details <span className="text-red-500">*</span>
                      </>
                    ) : (
                      <>
                        Notes{' '}
                        <span className="font-normal text-gray-400">
                          (optional)
                        </span>
                      </>
                    )}
                  </label>
                  <textarea
                    id="lost-reason-notes"
                    rows={3}
                    value={lostReasonNotes}
                    onChange={(event) => setLostReasonNotes(event.target.value)}
                    placeholder={
                      notesRequired
                        ? 'Describe why this lead was lost…'
                        : 'Any extra context from the conversation…'
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
                  />
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!canSubmit}
                    onClick={() =>
                      onConfirm({
                        lostReason: lostReason as LeadLostReason,
                        lostReasonNotes: lostReasonNotes.trim(),
                      })
                    }
                    className="rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? 'Closing…' : 'Close lead'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
